import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchClaimLinkTemplateByIdRequest } from '../store/actions/templates';
import { selectClaimLinkTemplate, selectClaimLinkTemplates } from '../store/selectors/templates';
import { IClaimLinkTemplate } from '../types/templates';

/**
 * Resolves a claim link template by ID.
 * Looks up the template in the paginated list first, then falls back to the
 * single-fetched `currentTemplate` in Redux. Automatically dispatches a fetch
 * request when the template is not yet in the store.
 */
export const useClaimLinkTemplate = (experienceId: string | null | undefined): IClaimLinkTemplate | null => {
  const dispatch = useDispatch();
  const claimLinkTemplates = useSelector(selectClaimLinkTemplates);
  const currentTemplate = useSelector(selectClaimLinkTemplate);

  const template = useMemo(() => {
    if (!experienceId) {
      return null;
    }
    const fromList = claimLinkTemplates?.find((t) => t.uid === experienceId);
    return fromList ?? (currentTemplate?.uid === experienceId ? currentTemplate : null);
  }, [experienceId, claimLinkTemplates, currentTemplate]);

  useEffect(() => {
    if (experienceId && !template) {
      dispatch(fetchClaimLinkTemplateByIdRequest({ id: experienceId }));
    }
  }, [experienceId, template, dispatch]);

  return template;
};
